import React, { useState, useRef, useContext } from "react";
import styled from "@mui/system/styled";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import { ISXContext } from "services/Utils";

const uuidv4 = require("uuid/v4");

const WIDGET_GROUPS = [
  "Single Value",
  "Time Series",
  "Analytics",
  "Administration",
  "Other",
];

const groupWidgets = (context) => {
  const widgetsByGroup = Object.entries(context.widgets).reduce(
    (acc, entry) => {
      const widget = entry[1];
      if (widget.addable || widget.addable == null) {
        let group = acc[widget.group];
        if (!group) {
          group = acc[widget.group] = [];
        }
        group.push(entry);
      }
      return acc;
    },
    {}
  );
  Object.values(widgetsByGroup).forEach((group) =>
    group.sort((e1, e2) => {
      return e1[1].displayName < e2[1].displayName ? -1 : 1;
    })
  );
  return widgetsByGroup;
};

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const NewWidgetDialog = (props) => {
  const [widgetType, setWidgetType] = useState("");
  const [nextDisabled, setNextDisabled] = useState(true);

  const isxContext = useContext(ISXContext);

  // initialize once
  const widgetsByGroup = useRef(null);
  if (!widgetsByGroup.current) {
    widgetsByGroup.current = groupWidgets(isxContext);
  }

  const setWidget = (event) => {
    setWidgetType(event.target.value);
    setNextDisabled(false);
  };

  const resetState = () => {
    setWidgetType("");
    setNextDisabled(true);
  };

  const reset = () => {
    props.removeDialog();
    resetState();
  };

  const addWidget = () => {
    const widgetInfo = isxContext.widgets[widgetType] || {};
    const dtype = widgetInfo.dataType;
    const widget = {
      guuid: uuidv4(),
      type: widgetType,
      options: {},
    };
    if (dtype) {
      widget[dtype] = [];
    }
    const defaultTitle = widgetInfo.displayName;
    if (defaultTitle) {
      widget.default_title = defaultTitle;
    }
    props.removeDialog();
    props.addWidget(widget);
    reset();
  };

  return (
    <Dialog open={props.open} aria-hidden={!props.open}>
      <DialogTitle>
        Add New Widget
        <CloseButton onClick={reset} size="large">
          <ClearIcon />
        </CloseButton>
      </DialogTitle>
      <DialogContent dividers>
        {WIDGET_GROUPS.map(
          (groupName) =>
            widgetsByGroup.current[groupName] && (
              <FormControl
                variant="standard"
                key={groupName}
                sx={{ margin: 2 }}
              >
                <FormLabel component="legend">{groupName}</FormLabel>
                <RadioGroup
                  key={groupName}
                  onChange={setWidget}
                  value={widgetType}
                >
                  {widgetsByGroup.current[groupName].map((entry) => {
                    const [widgetKey, widget] = entry;
                    const label = widget.preview ? (
                      <div>
                        {widget.displayName} <sup>preview</sup>
                      </div>
                    ) : (
                      widget.displayName
                    );
                    return (
                      <FormControlLabel
                        key={widgetKey}
                        value={widgetKey}
                        control={<Radio />}
                        label={label}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )
        )}
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: "red" }} onClick={reset}>
          Cancel
        </Button>
        <Button
          onClick={addWidget}
          disabled={nextDisabled}
          variant="contained"
          color="primary"
        >
          Finish
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewWidgetDialog;
